import React, { createContext, useCallback, useState, useEffect } from 'react';
import { LinearProgress, Box } from '@mui/material';
import type { LoadingOverlayContextValue } from './types';
import { setGlobalLoadingHandlers } from './loadingHandler';

const LoadingOverlayContext = createContext<
  LoadingOverlayContextValue | undefined
>(undefined);

interface LoadingOverlayProviderProps {
  children: React.ReactNode;
}

export function LoadingOverlayProvider({
  children,
}: LoadingOverlayProviderProps) {
  const [requestCount, setRequestCount] = useState(0);

  const increment = useCallback(() => {
    setRequestCount(prev => prev + 1);
  }, []);

  const decrement = useCallback(() => {
    setRequestCount(prev => Math.max(0, prev - 1));
  }, []);

  const isLoading = requestCount > 0;

  // Set up global loading handlers
  useEffect(() => {
    setGlobalLoadingHandlers(increment, decrement);

    return () => {
      setGlobalLoadingHandlers(null, null);
    };
  }, [increment, decrement]);

  const value: LoadingOverlayContextValue = {
    isLoading,
    requestCount,
    increment,
    decrement,
  };

  return (
    <LoadingOverlayContext.Provider value={value}>
      {children}

      {/* Global loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9998, // Below alerts (9999)
            height: 4,
          }}
        >
          <LinearProgress
            sx={{
              height: '100%',
              '& .MuiLinearProgress-bar': {
                transition: 'none', // Smooth continuous loading
              },
            }}
          />
        </Box>
      )}
    </LoadingOverlayContext.Provider>
  );
}

export { LoadingOverlayContext };
